"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Copy, CheckCircle, Clock, Calendar, ChevronDown, Edit, Check, Linkedin, Loader2 } from 'lucide-react'
import { Textarea } from "@/components/ui/textarea"
import { useGetOutreach } from "openapi/api/endpoints"
import { useParams } from "@tanstack/react-router"

interface CandidateLinkedInOutreachProps {
    candidate: {
        id?: string
        full_name?: string
        name?: string
        first_name?: string
        headline?: string
        total_experience_duration_months?: number
        inferred_skills?: string[]
        experience?: Array<{
            position_title?: string
            company_name?: string
        }>
    }
}

export function CandidateLinkedInOutreach({ candidate }: CandidateLinkedInOutreachProps) {
    const [isSent, setIsSent] = useState(false)
    const [isCopied, setIsCopied] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const routeParams = useParams({ strict: false }) as Record<string, string | undefined>
    const candidateId = routeParams.candidateId ?? routeParams.candidate_id ?? candidate?.id ?? ""
    const jobId = routeParams.jobId ?? routeParams.job_id ?? ""
    const { data, refetch, isRefetching, isFetching } = useGetOutreach({
        candidate_id: candidateId,
        job_id: jobId,
        type: 'linkedin',
    }, {
        query: {
            enabled: Boolean(candidateId && jobId),
            refetchOnWindowFocus: false,
            staleTime: 24 * 60 * 60 * 1000,
        }
    })
    const [messageContent, setMessageContent] = useState('')


    useEffect(() => {
        if (data) {
            setMessageContent(data.body || '')
        }
    }, [data])

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(messageContent)
            setIsCopied(true)
            setTimeout(() => setIsCopied(false), 2000)
        } catch (err) {
            console.error("[v0] Failed to copy:", err)
        }
    }

    const handleMarkAsSent = () => {
        setIsSent(true)
        console.log("[v0] LinkedIn message marked as sent")
    }

    const handleEdit = () => {
        setIsEditing(true)
    }

    const handleSaveEdit = () => {
        setIsEditing(false)
    }

    return (
        <div className="max-w-full">
            <Card className="border-l-4 border-l-blue-600">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Linkedin className="h-5 w-5 text-blue-600" />
                            LinkedIn Outreach
                        </CardTitle>
                        {isSent && (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Sent
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* AI Context Card */}
                    <Card className="bg-muted/50">
                        <CardHeader className="pb-3">
                            <div className="flex items-center gap-2">
                                <ChevronDown className="h-4 w-4" />
                                <CardTitle className="text-sm">AI Generation Context</CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm">
                            <div>
                                <Label className="font-medium">Candidate Profile</Label>
                                <p className="text-muted-foreground">Role: {candidate.headline}</p>
                                <p className="text-muted-foreground">
                                    Experience:{" "}
                                    {candidate.total_experience_duration_months
                                        ? Math.floor(candidate.total_experience_duration_months / 12)
                                        : 0}{" "}
                                    years in {candidate.experience?.[0]?.position_title?.toLowerCase()}
                                </p>
                                <p className="text-muted-foreground">
                                    Skills: {candidate.inferred_skills?.slice(0, 3).join(", ")}
                                </p>
                            </div>

                            <div>
                                <Label className="font-medium">Keywords Used</Label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {["connect", "opportunities", "career goals", "expertise"].map((keyword) => (
                                        <Badge key={keyword} variant="secondary" className="text-xs">
                                            {keyword}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label className="font-medium">Personalization Factors</Label>
                                <ul className="text-muted-foreground text-xs space-y-1 mt-1">
                                    <li>• Current company and role mentioned</li>
                                    <li>• Top skill highlighted from profile</li>
                                    <li>• Professional yet friendly tone</li>
                                    <li>• Clear call-to-action for connection</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Message Content */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="linkedin-message">Message Content:</Label>
                            <div className="flex gap-2">
                                {!isEditing ? (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleEdit}
                                        className="h-7 px-2 text-xs"
                                    >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Edit
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleSaveEdit}
                                        className="h-7 px-2 text-xs text-green-600"
                                    >
                                        <Check className="h-3 w-3 mr-1" />
                                        Save
                                    </Button>
                                )}
                            </div>
                        </div>
                        {isEditing ? (
                            <Textarea
                                id="linkedin-message"
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                className="min-h-[280px] font-sans text-sm bg-white dark:bg-background"
                            />
                        ) : (
                            <div className="relative">
                                <div className="p-4 border rounded-lg bg-white dark:bg-background min-h-[280px] text-sm whitespace-pre-wrap font-sans">
                                    {messageContent}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleCopy}
                                    className="absolute top-2 right-2 h-7 px-2"
                                >
                                    {isCopied ? (
                                        <>
                                            <Check className="h-3 w-3 mr-1" />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="h-3 w-3 mr-1" />
                                            Copy
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Character count: {messageContent.length} / 300 {messageContent.length > 300 && "(LinkedIn limit exceeded)"}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>Generated: {new Date().toLocaleString()}</span>
                        <Calendar className="h-3 w-3 ml-4" />
                        <span>Last updated: {new Date().toLocaleString()}</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2 border-t">
                        <Button
                            className={isSent ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}
                            onClick={handleMarkAsSent}
                            disabled={isSent || isFetching || isRefetching}
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {isSent ? "Marked as Sent" : "Mark as Sent"}
                        </Button>
                        <Button
                            variant="secondary"
                            disabled={isRefetching}
                            onClick={() => refetch()}
                            className="flex items-center gap-2"
                        >
                            {isRefetching ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Regenerate"
                            )}
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={handleCopy}
                            disabled={isFetching || isRefetching}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Message
                        </Button>
                    </div>

                    {/* LinkedIn Tips */}
                    <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                        <CardContent className="pt-4">
                            <div className="flex items-start gap-2">
                                <Linkedin className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                                    <p className="font-medium">LinkedIn InMail Tips:</p>
                                    <ul className="list-disc list-inside space-y-0.5 text-blue-700 dark:text-blue-300">
                                        <li>Keep messages under 300 characters for higher response rates</li>
                                        <li>Personalize the message with specific details from their profile</li>
                                        <li>Include a clear call-to-action</li>
                                        <li>Follow up within 3-5 days if no response</li>
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </CardContent>
            </Card>
        </div>
    )
}
